import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import type { RadioProps } from "@mui/material";
import { Tooltip } from "@mui/material";
import type { FormikValues } from "formik";
import type { ReactNode } from "react";
import { useMemo } from "react";

import type { RadioGroupItem, RadioGroupProps, RadioRef } from "./radio-group";
import * as sx from "./tri-switch.sx";

type TriSwitchOptionProps<Value extends string> = Omit<
  RadioGroupItem<Value>,
  "children" | "control" | "label"
> & {
  /** even though we hide the label, we use it as an aria-label */
  label: string;
};

export interface TriSwitchOptionsMap<Value extends string> {
  neutral: TriSwitchOptionProps<Value>;
  on: TriSwitchOptionProps<Value>;
  off: TriSwitchOptionProps<Value>;
}

export type TriSwitchProps<
  Shape extends FormikValues,
  Value extends string,
> = Omit<RadioGroupProps<Shape, Value>, "options" | "size"> & {
  optionsMap: TriSwitchOptionsMap<Value>;
  ref?: React.ForwardedRef<RadioRef>;
  tooltip?: ReactNode;
};

export function TriSwitch<
  Shape extends FormikValues,
  Value extends string = string,
>({ optionsMap, tooltip, ...rest }: TriSwitchProps<Shape, Value>) {
  const options = useMemo(
    () => [
      {
        ...optionsMap.on,
        control: (
          <Radio
            color={optionsMap.on.color ?? "success"}
            icon={<CheckIcon />}
            label={optionsMap.on.label}
            tooltip={tooltip}
          />
        ),
      },
      {
        ...optionsMap.neutral,
        control: (
          <Radio
            color={optionsMap.neutral.color ?? "info"}
            icon={<RemoveIcon />}
            label={optionsMap.neutral.label}
            tooltip={tooltip}
          />
        ),
      },
      {
        ...optionsMap.off,
        control: (
          <Radio
            color={optionsMap.off.color ?? "error"}
            icon={<CloseIcon />}
            label={optionsMap.off.label}
            tooltip={tooltip}
          />
        ),
      },
    ],
    [optionsMap.neutral, optionsMap.off, optionsMap.on, tooltip],
  );

  return (
    <sx.RadioGroup<Shape, Value> {...rest} size="small" options={options} />
  );
}

function Radio({
  color,
  icon,
  label,
  tooltip,
  // The `FormControlLabel` in our form `RadioGroup` component also passes in props,
  // which is why we need the `...rest`
  ...rest
}: {
  color: sx.RadioColor;
  icon: ReactNode;
  label: string;
  tooltip?: ReactNode;
} & RadioProps) {
  return (
    <Tooltip placement="top" title={tooltip ?? label}>
      <div>
        <sx.Radio
          {...rest}
          color={color}
          checkedIcon={icon}
          icon={icon}
          size="small"
          slotProps={{ input: { "aria-label": label } }}
        />
      </div>
    </Tooltip>
  );
}
